/**
 * AI PII Masking Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * GDPR PII masking configuration.
 */

import React from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';
import type { AISettingsUpdateData, PIIMaskingMode, PIISensitivity } from '../../../types/admin';
import {
  PII_MASKING_MODE_LABELS,
  PII_MASKING_MODE_DESCRIPTIONS,
  PII_SENSITIVITY_LABELS,
  PII_SENSITIVITY_DESCRIPTIONS,
} from '../../../types/admin';

interface AIPIIMaskingSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const MASKING_MODES: PIIMaskingMode[] = ['redact', 'pseudonymize', 'hash'];
const SENSITIVITY_LEVELS: PIISensitivity[] = ['low', 'medium', 'high'];

const PII_CATEGORIES = [
  {
    key: 'mask_personal_identifiers' as const,
    label: 'Personal Identifiers',
    description: 'SSN, passport numbers, tax IDs, national ID numbers',
    examples: 'XXX-XX-XXXX, 756.XXXX.XXXX.XX',
  },
  {
    key: 'mask_financial_data' as const,
    label: 'Financial Data',
    description: 'IBANs, credit card numbers, bank account numbers',
    examples: 'CH** **** **** **** ****',
  },
  {
    key: 'mask_contact_info' as const,
    label: 'Contact Information',
    description: 'Phone numbers, email addresses, physical addresses',
    examples: '+41 ** *** ** **, u***@example.com',
  },
  {
    key: 'mask_demographic_data' as const,
    label: 'Demographic Data',
    description: 'Date of birth, age, nationality, occupation',
    examples: '**.**.1980, [NATIONALITY]',
  },
];

export const AIPIIMaskingSettings: React.FC<AIPIIMaskingSettingsProps> = ({
  formData,
  onChange,
}) => {
  const scanOutput = formData.scan_output_for_pii ?? true;
  const blockOutput = formData.block_pii_in_output ?? true;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">PII Masking (GDPR Article 25)</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure how personally identifiable information is protected when sent to the AI model.
          This implements data protection by design as required by GDPR.
        </p>
      </div>

      {/* PII Categories */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Categories to Mask</h4>
        {PII_CATEGORIES.map((category) => {
          const isEnabled = formData[category.key] ?? true;

          return (
            <label
              key={category.key}
              className={`
                flex items-start justify-between p-4 rounded-lg border cursor-pointer transition-colors
                ${isEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{category.label}</span>
                  {isEnabled ? (
                    <EyeOff size={14} className="text-green-600" />
                  ) : (
                    <Eye size={14} className="text-gray-400" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{category.description}</p>
                <p className="text-xs text-gray-400 mt-1">Example: {category.examples}</p>
              </div>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => onChange({ [category.key]: e.target.checked })}
                className="ml-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
            </label>
          );
        })}
      </div>

      {/* Masking Mode */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Masking Mode</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MASKING_MODES.map((mode) => (
            <label
              key={mode}
              className={`
                p-4 rounded-lg border cursor-pointer transition-colors
                ${formData.pii_masking_mode === mode ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="radio"
                  name="pii_masking_mode"
                  value={mode}
                  checked={formData.pii_masking_mode === mode}
                  onChange={() => onChange({ pii_masking_mode: mode })}
                  className="text-[#002B45] focus:ring-[#F7B500]"
                />
                <span className="font-medium text-gray-900">{PII_MASKING_MODE_LABELS[mode]}</span>
              </div>
              <p className="text-xs text-gray-500 ml-6">{PII_MASKING_MODE_DESCRIPTIONS[mode]}</p>
            </label>
          ))}
        </div>
      </div>

      {/* Detection Sensitivity */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Detection Sensitivity</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SENSITIVITY_LEVELS.map((level) => (
            <label
              key={level}
              className={`
                p-4 rounded-lg border cursor-pointer transition-colors
                ${formData.pii_detection_sensitivity === level ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="radio"
                  name="pii_sensitivity"
                  value={level}
                  checked={formData.pii_detection_sensitivity === level}
                  onChange={() => onChange({ pii_detection_sensitivity: level })}
                  className="text-[#002B45] focus:ring-[#F7B500]"
                />
                <span className="font-medium text-gray-900">{PII_SENSITIVITY_LABELS[level]}</span>
              </div>
              <p className="text-xs text-gray-500 ml-6">{PII_SENSITIVITY_DESCRIPTIONS[level]}</p>
            </label>
          ))}
        </div>
      </div>

      {/* Output Scanning */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Output Protection</h4>
        <div className="space-y-3">
          <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
            <div className="flex items-center gap-3">
              <Shield size={18} className={scanOutput ? 'text-green-600' : 'text-gray-400'} />
              <div>
                <span className="font-medium text-gray-900">Scan AI Output for PII</span>
                <p className="text-sm text-gray-500">
                  Check AI responses for accidental PII leakage
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={scanOutput}
              onChange={(e) => onChange({ scan_output_for_pii: e.target.checked })}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
          </label>

          <label
            className={`
              flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors
              ${!scanOutput ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-300'}
              ${blockOutput && scanOutput ? 'border-green-200 bg-green-50' : 'border-gray-200'}
            `}
          >
            <div className="flex items-center gap-3">
              <EyeOff
                size={18}
                className={blockOutput && scanOutput ? 'text-green-600' : 'text-gray-400'}
              />
              <div>
                <span className="font-medium text-gray-900">Block PII in Output</span>
                <p className="text-sm text-gray-500">
                  Automatically redact detected PII from AI responses
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={blockOutput}
              onChange={(e) => onChange({ block_pii_in_output: e.target.checked })}
              disabled={!scanOutput}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default AIPIIMaskingSettings;
